import request from '@/utils/request';

/**
 * 审批中心（wf-engine）接口封装
 *
 * 权限：接口均以 `.json` 结尾，走 B 端 `functionValidator`（商家端 14 个功能点 1420~1433）；
 * 赋权脚本 `api-code/xqyy_dao/src/main/resources/sql/wf_engine_permission.sql`，
 * 执行后必须同步 Manticore `XQYY:xqyyrolefun`，否则调用返回 110 无权限。
 *
 * 数据口径：`businessId` 与发起人一律取登录态（后端注入），前端**不传**。
 */

/** 我的待办分页（不传 taskStatus 默认只看「1 待处理」，传 2/3/4/5 可查历史） */
export function getWfTodoPage(data: { bizType?: string; taskStatus?: number; pageNo?: number; pageSize?: number }) {
    return request.http<Common.ListData<Wf.ITaskTodo>>({
        method: 'POST',
        url: '/wf/getTodoPage.json',
        data
    });
}

/** 我的已办分页 */
export function getWfDonePage(data: { bizType?: string; pageNo?: number; pageSize?: number }) {
    return request.http<Common.ListData<Wf.ITaskTodo>>({
        method: 'POST',
        url: '/wf/getDonePage.json',
        data
    });
}

/** 我发起的流程分页（approveStatus：0 未提交 / 1 审批中 / 2 通过 / 3 驳回） */
export function getWfMyInitiatedPage(data: {
    bizType?: string;
    approveStatus?: number;
    pageNo?: number;
    pageSize?: number;
}) {
    return request.http<Common.ListData<Wf.IInstance>>({
        method: 'POST',
        url: '/wf/getMyInitiatedPage.json',
        data
    });
}

/** 审批单详情（实例 + 节点任务进度 + 动作留痕） */
export function getWfDetail(data: { instanceId: number }) {
    return request.http<Wf.IInstanceDetail>({
        method: 'POST',
        url: '/wf/getDetail.json',
        data
    });
}

/** 审批通过 */
export function approveWfTask(data: { taskId: number; remark?: string }) {
    return request.http<boolean>({
        method: 'POST',
        url: '/wf/approve.json',
        data
    });
}

/** 审批驳回 */
export function rejectWfTask(data: { taskId: number; remark?: string }) {
    return request.http<boolean>({
        method: 'POST',
        url: '/wf/reject.json',
        data
    });
}

/** 转交（转交后原任务取消，新任务落到 targetUserId） */
export function transferWfTask(data: { taskId: number; targetUserId: string | number; remark?: string }) {
    return request.http<boolean>({
        method: 'POST',
        url: '/wf/transfer.json',
        data
    });
}

/** 催办（发起人对在途实例的当前待办人催办，每人每天最多一次） */
export function urgeWfInstance(data: { instanceId: number }) {
    return request.http<boolean>({
        method: 'POST',
        url: '/wf/urge.json',
        data
    });
}

/** 撤回（仅发起人 + 审批中实例可撤回） */
export function withdrawWfInstance(data: { instanceId: number; remark?: string }) {
    return request.http<boolean>({
        method: 'POST',
        url: '/wf/withdraw.json',
        data
    });
}

/**
 * 发起审批（幂等：同一 bizType + bizId 重复调用返回同一实例，撤回态则重新提交）
 *
 * 业务单据自带的发起入口（如请假提交）由后端在业务 Controller 内部直接调用 service，
 * 本接口用于审批中心手动补发起与联调验证。
 */
export function startWfInstance(data: Wf.IStartParam) {
    return request.http<Wf.IInstance>({
        method: 'POST',
        url: '/wf/start.json',
        data
    });
}

/** 审批流程定义分页（商家维度） */
export function getWfDefPage(data: { bizType?: string; keywords?: string; pageNo?: number; pageSize?: number }) {
    return request.http<Common.ListData<Wf.IDef>>({
        method: 'POST',
        url: '/wf/getDefPage.json',
        data
    });
}

/** 审批流程定义详情（定义 + 节点链 + 是否已被实例引用） */
export function getWfDefDetail(data: { id: number }) {
    return request.http<Wf.IDefDetail>({
        method: 'POST',
        url: '/wf/getDefDetail.json',
        data
    });
}

/** 保存审批流程定义（含节点链覆盖写；被在途实例引用的定义禁止改动节点链） */
export function saveWfDef(data: Wf.IDef & { nodes?: Wf.INode[] }) {
    return request.http<number>({
        method: 'POST',
        url: '/wf/saveDef.json',
        data
    });
}

/**
 * 审批流程定义状态变更（数据状态口径：1 正常 / 2 冻结 / 3 删除，删除仅在冻结态可见）
 */
export function updateWfDefStatus(data: { type: number; list: Array<{ id: number; updateCount?: number }> }) {
    return request.http<boolean>({
        method: 'POST',
        url: '/wf/updateDefStatus.json',
        data
    });
}

/**
 * 图模型流程定义草稿保存（def_type=2，graph_json 为唯一数据源）
 *
 * 后端 saveDefGraph 内部做图校验（规则与前端 designerV2/graphV2#validateGraph 同口径）；
 * 既有已发布定义保存草稿只更新 graph_json（发布状态不变，发起仍走发布快照）。
 * 入参携带 graphJson（WfGraph JSON 字符串），返回定义 id。
 */
export function saveWfDefGraph(data: Wf.IDef & { graphJson: string }) {
    return request.http<number>({
        method: 'POST',
        url: '/wf/saveDefGraph.json',
        data
    });
}

/** 发布图模型流程定义（生成版本快照、version_no+1；有在途实例允许发布，实例绑定旧快照不受影响） */
export function publishWfDef(data: Wf.IPublishParam) {
    return request.http<Wf.IPublishResult>({
        method: 'POST',
        url: '/wf/publishDef.json',
        data
    });
}

/** 图模型流程定义详情（定义 + 草稿 graphJson + 发布版本列表 + 是否被在途实例引用） */
export function getWfDefGraphDetail(data: { id: number }) {
    return request.http<Wf.IGraphDefDetail>({
        method: 'POST',
        url: '/wf/getDefGraphDetail.json',
        data
    });
}
